import {
  debounce,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  makeStyles,
  OutlinedInput,
  Paper,
  TablePagination,
} from "@material-ui/core";
import {
  DataGrid,
  GridFooterContainer,
  GridValueFormatterParams,
} from "@material-ui/data-grid";
import { useCallback, useEffect, useState } from "react";
import { useDataProvider, useNotify } from "react-admin";
import ShowDetailButton from "./ShowDetailButton";
import ClearIcon from "@material-ui/icons/Clear";
import ManualStatusField from "./ManualStatusField";

interface IPostedInvoicesTable {
  mbcCustomerId?: string;
  isAutoHeight?: boolean;
  userId?: number;
  isNotHavePagination?: boolean;
}

const useStyles = makeStyles({
  tableWrapper: { height: 400, width: "100%" },
  tableRoot: {
    border: 0,
  },
  label: {
    backgroundColor: "#ffffff",
    padding: "0 10px",
  },
  filterRoot: {
    "& fieldset": {
      borderColor: "#EAEAEA",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#EAEAEA",
    },
  },
  formControl: {
    margin: 10,
  },
});

const PostedInvoicesTable = ({
  mbcCustomerId,
  isAutoHeight,
  userId,
  isNotHavePagination,
}: IPostedInvoicesTable) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [filterValue, setFilterValue] = useState<string>();

  const [nextLink, setNextLink] = useState<string>(null);
  const [customerLedgers, setCustomerLedgers] = useState<ICustomerLedger[]>([]);
  const [page, setPage] = useState<number>(0);
  const [baseData, setBaseData] = useState<IBaseDataTable[]>([]);
  const [disableNextPage, setDisableNextPage] = useState<boolean>(false);

  const [filteredNextLink, setFilteredNextLink] = useState<string>(null);
  const [filteredCustomerLedgers, setFilteredCustomerLedgers] = useState<
    ICustomerLedger[]
  >([]);
  const [filteredPage, setFilteredPage] = useState<number>(0);
  const [filteredBaseData, setFilteredBaseData] = useState<IBaseDataTable[]>(
    []
  );
  const [disableFilteredNextPage, setDisableFilteredNextPage] =
    useState<boolean>(false);

  const provider = useDataProvider();
  const notify = useNotify();
  const classes = useStyles();
  const projectCodeDefaultFilter = ["SCH-SUR","SCH-CCA","SCH-ADH","SCH-FIX"];

  const getCustomerLedgers = () => {
    setLoading(true);
    provider
      .customGetList("customerLedgers", {
        filter: {
          CustomerNo: mbcCustomerId,
          UserId: userId,
          DocumentType: "Invoice",
          ProjectCodes: projectCodeDefaultFilter
        },
      })
      .then(({ data }: any) => {
        const base: IBaseDataTable[] = [
          {
            page: 0,
            currentUrl: null,
            nextUrl: data.nextLink ? data.nextLink : null,
            data: data.values,
          },
        ];
        setBaseData(base);
        setLoading(false);
      })
      .catch((error: any) => {
        notify(`Error from MBC : ${error.message}`, "error");
        setLoading(false);
      });
  };

  const getCustomerLedgersWithNextLink = () => {
    setLoading(true);
    provider
      .customGetList("customerLedgers", {
        filter: { NextLink: nextLink },
      })
      .then(({ data }: any) => {
        const base: IBaseDataTable[] = [
          {
            page: page,
            currentUrl: null,
            nextUrl: data.nextLink ? data.nextLink : null,
            data: data.values,
          },
        ];
        setBaseData((oldBaseData: IBaseDataTable[]) => [
          ...oldBaseData,
          ...base,
        ]);

        setLoading(false);
      })
      .catch((error: any) => {
        notify(`Error from MBC : ${error.message}`, "error");
        setLoading(false);
      });
  };

  const findBaseDataByPage = () => {
    if (baseData.length > 0) {
      const findData = baseData.find(
        (item: IBaseDataTable) => item.page === page
      );

      if (findData) {
        setCustomerLedgers(findData.data);
        setNextLink(findData.nextUrl);
        setDisableNextPage(findData.nextUrl ? false : true);
      } else {
        getCustomerLedgersWithNextLink();
      }
    }
  };

  useEffect(() => {
    getCustomerLedgers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    findBaseDataByPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, baseData]);

  const customerLedgerColumns = [
    {
      field: "no",
      headerName: "Invoice Number",
      type: "string",
      flex: 1.15,
      filterable: false,
    },
    {
      field: "description",
      headerName: "Description",
      type: "string",
      flex: 2,
      filterable: false,
      allowResizing: true,
    },
    {
      field: "salesLcy",
      headerName: "Amount",
      type: "number",
      flex: 1,
      filterable: false,
      valueFormatter: (params: GridValueFormatterParams) =>
        `$ ${params.value.toLocaleString()}`,
    },
    {
      field: "amountLcy",
      headerName: "Amt. Include VAT",
      type: "number",
      flex: 1.25,
      filterable: false,
      valueFormatter: (params: GridValueFormatterParams) =>
        `$ ${params.value.toLocaleString()}`,
    },
    {
      field: "remainingAmount",
      headerName: "Remaining Amount",
      type: "number",
      flex: 1,
      filterable: false,
      valueFormatter: (params: GridValueFormatterParams) =>
        `$ ${params.value.toLocaleString()}`,
    },
    {
      field: "customerNo",
      headerName: "Customer Number",
      type: "string",
      flex: 1,
      filterable: false,
    },
    {
      field: "customerName",
      headerName: "Customer Name",
      type: "string",
      flex: 1.15,
      filterable: false,
    },
    {
      field: "open",
      headerName: "Status",
      flex: 0.9,
      filterable: false,
      renderCell: (params: any) => {
        if (params.value === true) {
          return <ManualStatusField value="open" />;
        } else {
          return <ManualStatusField value="closed" />;
        }
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      filterable: false,
      renderCell: ({ id, row }: any) => {
        return <ShowDetailButton detailId={id} />;
      },
    },
  ];

  const LoadMoreFooter = () => {
    const handleChangePage = (
      event: React.MouseEvent<HTMLButtonElement> | null,
      newPage: number
    ) => {
      setPage(newPage);
    };

    const handleChangeFilteredPage = (
      event: React.MouseEvent<HTMLButtonElement> | null,
      newPage: number
    ) => {
      console.log("TEST change page", newPage);
      setFilteredPage(newPage);
    };

    return (
      <GridFooterContainer>
        <div></div>

        <TablePagination
          component="div"
          count={
            filterValue?.length > 2
              ? filteredBaseData.reduce((acc: number, item: IBaseDataTable) => {
                  return acc + item.data.length;
                }, 0)
              : baseData.reduce((acc: number, item: IBaseDataTable) => {
                  return acc + item.data.length;
                }, 0)
          }
          nextIconButtonProps={{
            disabled: loading
              ? loading
              : filterValue?.length > 2
              ? disableFilteredNextPage
              : disableNextPage,
          }}
          page={filterValue?.length > 2 ? filteredPage : page}
          rowsPerPage={25}
          rowsPerPageOptions={[25]}
          onChangePage={
            filterValue?.length > 2
              ? handleChangeFilteredPage
              : handleChangePage
          }
        />
      </GridFooterContainer>
    );
  };

  const getFilteredCustomerLedgers = (filterValue: string) => {
    provider
      .customGetList("customerLedgers", {
        filter: {
          CustomerNo: mbcCustomerId,
          UserId: userId,
          DocumentType: "Invoice",
          ProjectCodes: projectCodeDefaultFilter,
          InvoiceNo: filterValue,
        },
      })
      .then(({ data }: any) => {
        const base: IBaseDataTable[] = [
          {
            page: 0,
            currentUrl: null,
            nextUrl: data.nextLink ? data.nextLink : null,
            data: data.values,
          },
        ];
        setFilteredBaseData(base);
        setLoading(false);
      })
      .catch((error: any) => {
        notify(`Error from MBC : ${error.message}`, "error");
        setLoading(false);
      });
  };

  const getFilteredCustomerLedgersWithNextLink = () => {
    setLoading(true);
    provider
      .customGetList("customerLedgers", {
        filter: { NextLink: filteredNextLink },
      })
      .then(({ data }: any) => {
        const base: IBaseDataTable[] = [
          {
            page: filteredPage,
            currentUrl: null,
            nextUrl: data.nextLink ? data.nextLink : null,
            data: data.values,
          },
        ];
        setFilteredBaseData((oldBaseData: IBaseDataTable[]) => [
          ...oldBaseData,
          ...base,
        ]);

        setLoading(false);
      })
      .catch((error: any) => {
        notify(`Error from MBC : ${error.message}`, "error");
        setLoading(false);
      });
  };

  const findFilteredBaseDataByPage = () => {
    if (filteredBaseData.length > 0) {
      const findData = filteredBaseData.find(
        (item: IBaseDataTable) => item.page === filteredPage
      );

      if (findData) {
        setFilteredCustomerLedgers(findData.data);
        setFilteredNextLink(findData.nextUrl);
        setDisableFilteredNextPage(findData.nextUrl ? false : true);
      } else {
        getFilteredCustomerLedgersWithNextLink();
      }
    }
  };

  useEffect(() => {
    findFilteredBaseDataByPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPage, filteredBaseData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onFilterChange = useCallback(
    debounce((filterValue: string) => {
      getFilteredCustomerLedgers(filterValue);
    }, 1000),
    []
  );

  useEffect(() => {
    if (
      filterValue !== undefined &&
      filterValue !== "" &&
      filterValue?.length > 2
    ) {
      setLoading(true);
      onFilterChange(filterValue);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);

  const handleClearValue = () => {
    setFilterValue("");
  };

  return (
    <Paper elevation={1}>
      <FormControl variant="outlined" className={classes.formControl}>
        <InputLabel htmlFor="outlined-adornment" className={classes.label}>
          Invoice No
        </InputLabel>
        <OutlinedInput
          id="outlined-adornment"
          className={classes.filterRoot}
          value={filterValue}
          onChange={({ target }) => {
            console.log("TEST", target.value);
            setFilterValue(target.value);
          }}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear text input"
                onClick={handleClearValue}
                edge="end"
              >
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>

      {isAutoHeight ? (
        <DataGrid
          className={classes.tableRoot}
          rows={
            filterValue?.length > 2 ? filteredCustomerLedgers : customerLedgers
          }
          columns={customerLedgerColumns}
          components={{
            Footer: isNotHavePagination ? null : LoadMoreFooter,
          }}
          filterMode="server"
          loading={loading}
          disableColumnMenu
          sortModel={[{ field: "no", sort: "desc" }]}
          autoHeight
        ></DataGrid>
      ) : (
        <div className={classes.tableWrapper}>
          <DataGrid
            className={classes.tableRoot}
            rows={
              filterValue?.length > 2
                ? filteredCustomerLedgers
                : customerLedgers
            }
            columns={customerLedgerColumns}
            components={{
              Footer: isNotHavePagination ? null : LoadMoreFooter,
            }}
            filterMode="server"
            loading={loading}
            disableColumnMenu
            sortModel={[{ field: "no", sort: "desc" }]}
          ></DataGrid>
        </div>
      )}
    </Paper>
  );
};

export default PostedInvoicesTable;
